import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  VpnKey as VpnKeyIcon,
  AdminPanelSettings as AdminIcon,
  Public as PublicIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ATSEntitlementSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/ats-system');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to ATS System"
        >
          Back to ATS
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            ATS Entitlement System
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Identity and Access Management Platform
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Subsystem Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The ATS Entitlement System is the centralized identity and access management (IAM) platform for all ATS 
            applications, providing role-based access control (RBAC), authentication, authorization, and user lifecycle 
            management. Integrated with Active Directory and DHS security infrastructure, the Entitlement System ensures 
            that only authorized personnel can access sensitive targeting data and capabilities based on their roles, 
            clearances, and operational needs, maintaining security and compliance across the entire ATS ecosystem.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Subsystem Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="ATS Entitlement System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is ATS Entitlement System"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>centralized identity and access management (IAM) platform</strong> that provides <strong>authentication, 
                    authorization, and role-based access control (RBAC)</strong> for all ATS subsystems. Manages <strong>user 
                    identities, permissions, security clearances, and access policies</strong> ensuring only authorized personnel 
                    can access sensitive targeting data and capabilities based on <strong>need-to-know and least privilege 
                    principles</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses ATS Entitlement System"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Managed by <strong>CBP IT Security and ATS Program Office</strong>. Serves <strong>10,000+ ATS users</strong> 
                    including <strong>National Targeting Center analysts, port-level officers, Border Patrol agents, CBP management, 
                    partner agency personnel</strong> (ICE, TSA, FBI), and <strong>system administrators</strong>. Enforces access 
                    policies for all ATS subsystems (ATS-C, ATS-P, ATS-L, ATS-I, ATS-G).
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was ATS Entitlement System deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Initial deployment in <strong>2005-2007</strong> as ATS expanded. Major upgrades in <strong>2010-2012</strong> 
                    with RBAC implementation, <strong>2015-2017</strong> with Active Directory integration, and <strong>2019-2021</strong> 
                    with multi-factor authentication (MFA) and continuous monitoring. Operates <strong>24/7/365</strong> with 
                    real-time access control enforcement and <strong>quarterly access reviews</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is ATS Entitlement System deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Centralized IAM infrastructure</strong> hosted in secure CBP data centers with <strong>redundant 
                    authentication servers</strong>. Integrated with <strong>DHS Active Directory, PIV card systems, and 
                    security clearance databases</strong>. Enforces access control across <strong>all ATS deployment locations</strong> 
                    including NTC, 328 ports of entry, and international CSI ports.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why ATS Entitlement System exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To <strong>protect sensitive targeting data and capabilities</strong> by ensuring only authorized personnel 
                    with appropriate clearances and need-to-know can access ATS systems. Critical for <strong>national security, 
                    privacy protection, and compliance</strong> with federal security requirements (FISMA, NIST 800-53, DHS 
                    4300A). Provides <strong>audit trails, access accountability, and insider threat detection</strong> across 
                    the ATS ecosystem.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core IAM Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VpnKeyIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Authentication & Authorization
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Secure identity verification and access control enforcement.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Multi-Factor Authentication:</strong> PIV card + PIN/biometric</Typography></li>
                <li><Typography variant="body2"><strong>Single Sign-On (SSO):</strong> Unified authentication across ATS subsystems</Typography></li>
                <li><Typography variant="body2"><strong>Active Directory Integration:</strong> DHS enterprise identity management</Typography></li>
                <li><Typography variant="body2"><strong>Session Management:</strong> Timeout enforcement and concurrent session control</Typography></li>
                <li><Typography variant="body2"><strong>Access Token Management:</strong> Secure credential issuance and validation</Typography></li>
                <li><Typography variant="body2"><strong>Password Policies:</strong> Complexity requirements and rotation enforcement</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AdminIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Role-Based Access Control (RBAC)
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Granular permission management based on user roles and responsibilities.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Role Definitions:</strong> 50+ predefined roles (analyst, officer, admin)</Typography></li>
                <li><Typography variant="body2"><strong>Permission Sets:</strong> Granular access to data and functions</Typography></li>
                <li><Typography variant="body2"><strong>Clearance Levels:</strong> Secret, Top Secret, SCI access enforcement</Typography></li>
                <li><Typography variant="body2"><strong>Need-to-Know:</strong> Compartmentalized information access</Typography></li>
                <li><Typography variant="body2"><strong>Least Privilege:</strong> Minimum necessary access rights</Typography></li>
                <li><Typography variant="body2"><strong>Separation of Duties:</strong> Prevent conflicts of interest</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShieldIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  User Lifecycle Management
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Complete user account management from onboarding to offboarding.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Account Provisioning:</strong> Automated user creation and role assignment</Typography></li>
                <li><Typography variant="body2"><strong>Access Requests:</strong> Workflow-based approval process</Typography></li>
                <li><Typography variant="body2"><strong>Role Changes:</strong> Transfer, promotion, and reassignment handling</Typography></li>
                <li><Typography variant="body2"><strong>Access Reviews:</strong> Quarterly recertification of user permissions</Typography></li>
                <li><Typography variant="body2"><strong>Account Deactivation:</strong> Immediate revocation upon separation</Typography></li>
                <li><Typography variant="body2"><strong>Audit Logging:</strong> Complete lifecycle tracking and reporting</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Security & Compliance
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Comprehensive security monitoring and regulatory compliance.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Continuous Monitoring:</strong> Real-time access pattern analysis</Typography></li>
                <li><Typography variant="body2"><strong>Anomaly Detection:</strong> Unusual access behavior alerts</Typography></li>
                <li><Typography variant="body2"><strong>Insider Threat Detection:</strong> Suspicious activity identification</Typography></li>
                <li><Typography variant="body2"><strong>Compliance Reporting:</strong> FISMA, NIST 800-53, DHS 4300A</Typography></li>
                <li><Typography variant="body2"><strong>Audit Trails:</strong> Complete access history and accountability</Typography></li>
                <li><Typography variant="body2"><strong>Privacy Protection:</strong> PII access logging and monitoring</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Roles */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AdminIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        ATS User Roles & Permissions
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Clearance Required</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Access Level</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Key Permissions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>NTC Analyst</strong></TableCell>
              <TableCell>Top Secret/SCI</TableCell>
              <TableCell>Full targeting access</TableCell>
              <TableCell>View/create targets, intelligence data, all subsystems</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Port Officer</strong></TableCell>
              <TableCell>Secret</TableCell>
              <TableCell>Operational access</TableCell>
              <TableCell>View targets, exam results, port-specific data</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Supervisor</strong></TableCell>
              <TableCell>Secret/Top Secret</TableCell>
              <TableCell>Management access</TableCell>
              <TableCell>Approve targets, view reports, manage team access</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Partner Agency User</strong></TableCell>
              <TableCell>Varies by agency</TableCell>
              <TableCell>Limited access</TableCell>
              <TableCell>View shared targets, submit referrals</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>System Administrator</strong></TableCell>
              <TableCell>Top Secret</TableCell>
              <TableCell>Administrative access</TableCell>
              <TableCell>User management, system config, audit logs</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Auditor</strong></TableCell>
              <TableCell>Secret</TableCell>
              <TableCell>Read-only access</TableCell>
              <TableCell>View audit logs, compliance reports, access reviews</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Scale & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                10K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Users
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                ATS user accounts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                50+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                User Roles
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Defined role types
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                99.9%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                System Availability
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                24/7 uptime
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                100%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Access Logged
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Complete audit trail
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Security & Compliance Value
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Data Protection:</strong> Secure sensitive targeting information</Typography></li>
                <li><Typography variant="body2"><strong>Access Control:</strong> Enforce need-to-know and least privilege</Typography></li>
                <li><Typography variant="body2"><strong>Accountability:</strong> Complete audit trails for all access</Typography></li>
                <li><Typography variant="body2"><strong>Insider Threat:</strong> Detect and prevent unauthorized access</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Compliance:</strong> Meet FISMA, NIST, DHS security requirements</Typography></li>
                <li><Typography variant="body2"><strong>Efficiency:</strong> Single sign-on and automated provisioning</Typography></li>
                <li><Typography variant="body2"><strong>Privacy:</strong> Protect PII with access monitoring</Typography></li>
                <li><Typography variant="body2"><strong>Scalability:</strong> Support growing user base and systems</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration Points */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Integrations
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="Active Directory" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">DHS enterprise identity</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="PIV Card System" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Smart card authentication</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Security Clearance DB" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Clearance verification</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="SIEM" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Security monitoring</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="All ATS Subsystems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">ATS-C, ATS-P, ATS-L, ATS-I, ATS-G</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="HR Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Employee lifecycle</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Audit Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Compliance reporting</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Partner Agency IAM" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Federated identity</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ATSEntitlementSystemDetail;
